<template>
  <div class="navbar">
    <StationStatusbar v-if="hasStationId()" />
    <div class="navbar-right">
      <div class="navbar-item navbar-theme">
        <Sun />
      </div>
      <div class="v-line"></div>
      <div class="v-line"></div>
      <div v-ripple="{ class: 'text-indigo-lighten-4' }" class="navbar-item navbar-fullscreen"
        v-tooltip:left="isFullscreen() ? '退出全屏' : '全屏'" @click="toggleFullscreen">
        <Maximize />
      </div>
      <div class="v-line"></div>
      <div class="navbar-time">{{ currentTime }}</div>

      <v-menu open-on-hover>
        <template v-slot:activator="{ props }">
          <v-badge class="user-avatar-badge" :content="applicationCount"
            :model-value="hasStationRole && applicationCount > 0"
            color="error" location="top right" offset-x="-6" offset-y="-2">
            <img src="@/assets/images/user.svg" width="22" height="28" v-bind="props">
          </v-badge>
        </template>
        <v-list>
          <v-list-item>
            <v-btn text="个人信息" variant="flat" @click="router.push('/personal/info')"></v-btn>
          </v-list-item>
          <v-list-item v-if="hasStationRole">
            <v-badge class="my-stations-badge" dot :model-value="applicationCount > 0" color="error"
              location="top right" offset-x="4" offset-y="4">
              <v-btn text="电站审批" variant="flat" @click="router.push('/personal/stations')"></v-btn>
            </v-badge>
          </v-list-item>
          <v-list-item>
            <v-dialog max-width="450">
              <template #activator="{ props: activatorProps }">
                <v-btn v-bind="activatorProps" text="退出" variant="flat"></v-btn>
              </template>

              <template #default="{ isActive }">
                <v-card class="gm-dialog-card" rounded="lg">
                  <v-card-title class="pb-0">
                    <div class="d-flex align-center ga-3">
                      <span class="text-h6">提示</span>
                    </div>
                  </v-card-title>

                  <v-card-text>
                    <div class="text-body-1" style="word-break: break-word;">
                      您确定要退出吗？
                    </div>
                  </v-card-text>

                  <v-card-actions class="justify-end pt-2 pb-4">
                    <v-btn text="暂不" @click="isActive.value = false"></v-btn>
                    <v-btn color="primary" variant="flat" @click="logout()">确定退出</v-btn>
                  </v-card-actions>
                </v-card>
              </template>
            </v-dialog>
          </v-list-item>
        </v-list>
      </v-menu>

    </div>
  </div>
</template>

<script setup>
import useUserStore from "@/store/modules/user";
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import router from "@/router";
import dayjs from "dayjs";
import { Sun, Maximize } from 'lucide-vue-next'
import { useRoute } from "vue-router";
import { getOMTodoList, getApprovedStationList } from "@/api/workbench/it";
import { checkRole } from "@/utils/permission";

import StationStatusbar from "./StationStatusBar/index.vue";

const userStore = useUserStore();
const route = useRoute();

/** 仅内部三个角色才能看到「我的电站」以及头像右上方角标 */
const STATION_ROLES = ['jd-develop', 'jd-project', 'jd-support']
const hasStationRole = computed(() => checkRole(STATION_ROLES))

function hasStationId() {
  return route.query.stationId !== undefined
}

function toggleFullscreen() {
  if (isFullscreen()) {
    document.exitFullscreen()
  } else {
    document.documentElement.requestFullscreen()
  }
}

function isFullscreen() {
  return document.fullscreenElement !== null
}


async function logout() {
  userStore.logOut().then(() => {
    router.push({ path: '/login' })
  })
}

const emits = defineEmits(["setLayout"]);

const now = ref(new Date())
const currentTime = computed(() => {
  return dayjs(now.value).format('HH:mm:ss')
})

let clockTimer = setInterval(() => {
  now.value = new Date()
}, 1000);

/** 头像角标：电站审批待办总数
 *  - jd-develop / jd-project：/device/v3/station/approval/approved/list 中 status !== 0 且 applicantRead === 0 的条数
 *  - jd-support（及其它命中 STATION_ROLES 的情况）：/device/v3/station/approval/todo/list 的 total
 *  同时命中时工程类优先（与 /personal/stations 保持一致）
 */
const applicationCount = ref(0)
async function fetchApplicationCount() {
  if (!hasStationRole.value) {
    applicationCount.value = 0
    return
  }
  try {
    if (checkRole(['jd-develop', 'jd-project'])) {
      const res = await getApprovedStationList({ pageNum: 1, pageSize: 999 })
      const rows = res?.rows || []
      applicationCount.value = rows.filter(row => row.status !== 0 && row.applicantRead === 0).length
    } else {
      const res = await getOMTodoList({ pageNum: 1, pageSize: 1 })
      applicationCount.value = res?.total ?? 0
    }
  } catch (err) {
    console.error('获取电站审批待办数量失败:', err)
    applicationCount.value = 0
  }
}
onMounted(() => {
  fetchApplicationCount()
})

// 每次进入页面（路由变化）都重新获取
watch(
  () => route.fullPath,
  () => {
    fetchApplicationCount()
  }
)

onUnmounted(() => {
  clearInterval(clockTimer)
})
</script>

<style lang='scss' scoped>
.navbar {
  --navbar-height: 70px;
  min-height: var(--navbar-height);
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;


  .navbar-right {
    max-height: 55px;
    background: rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-radius: 50px;
    padding: 8px 16px;
    margin-left: auto;
    margin-right: 16px;

    .v-line {
      width: 1px;
      height: 24px;
      background: #E5E5E5;
    }

    .navbar-item {
      min-width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      padding: 0 16px;
      cursor: pointer;
    }

    .navbar-time {
      padding: 8px 16px;
      width: 130px;
      font-family: 'Gotham-Book';
      font-style: normal;
      font-weight: 325;
      font-size: 24px;
      color: #2A2A2A;
    }
  }
}

.user-avatar-badge :deep(.v-badge__badge) {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  line-height: 16px;
}

.my-stations-badge {
  display: block;
}

.my-stations-badge :deep(.v-badge__wrapper) {
  width: 100%;
}
</style>
